import qs from 'qs';

const baseURL = '/dashboard';

export const propData = R.prop('data');

import memoizee from 'memoizee';
import { isValueExist } from '@/utils/utils';

function getGraph({
    metric = '',
    userAccountId = null,
    dateGroup = 'day',
    from = '2020-01-01',
    to = '2020-03-01',
    graphType = '',
    campaignIds = [],
    tracker = '',
    conversionName = '',
}) {
    const uri = `${baseURL}/graph`;
    return axios
        .get(uri, {
            params: {
                metric,
                userAccountId,
                dateGroup,
                from,
                to,
                graphType,
                campaignIds,
                tracker,
                conversionName,
            },
        })
        .then(propData);
}

function getPreview({ channelId = '', pageId = '', position = '' }) {
    const uri = `${baseURL}/creativePreview/${channelId}`;
    return axios
        .get(uri, {
            params: {
                pageId,
                position,
            },
        })
        .then(propData);
}

function getListFilterPreview({
    viewId,
    groupId,
    by,
    condition,
    limit = 50,
    offset = 0,
} = {}) {
    const uri = `${baseURL}/listFilter/preview`;

    return axios
        .post(uri, {
            viewId,
            groupId,
            by,
            condition,
            limit,
            offset,
        })
        .then(propData);
}

function saveFilters({ name, settings }) {
    if (!name || settings.length === 0) return;

    const uri = `${baseURL}/views`;

    return axios.post(uri, {
        name,
        settings,
    });
}

function getSavedFilters() {
    return axios.get(`${baseURL}/views`);
}

function deleteSavedFilter(viewId) {
    return axios.delete(`${baseURL}/views/${viewId}`);
}

function createPreset({ data, userAccountId }) {
    return axios
        .post(`${baseURL}/presets`, data, { params: { userAccountId } })
        .then(propData);
}

function getPresets(userAccountId) {
    return axios
        .get(`${baseURL}/presets`, { params: { userAccountId } })
        .then(propData);
}

function deletePreset(id) {
    return axios.delete(`${baseURL}/presets/${id}`).then(propData);
}

function getCustomBreakdowns({ viewId, teamspaceId } = {}) {
    return axios
        .get(`${baseURL}/customBreakdowns`, {
            params: {
                viewId,
                teamspaceId,
            },
        })
        .then(propData);
}

function searchCustomBreakdownLabels({ q, viewId, teamspaceId } = {}) {
    return axios
        .get(`${baseURL}/customBreakdowns/labels/search`, {
            params: {
                q,
                viewId,
                teamspaceId,
            },
        })
        .then(propData);
}

function getCustomBreakdownById({
    customBreakdownId,
    viewId,
    teamspaceId,
} = {}) {
    if (!customBreakdownId) {
        return;
    }

    return axios
        .get(`${baseURL}/customBreakdowns/${customBreakdownId}`, {
            params: {
                viewId,
                teamspaceId,
            },
        })
        .then(propData);
}

function resolveLabels({ labelIds, viewId, teamspaceId } = {}) {
    if (!labelIds || labelIds.length === 0) {
        return Promise.resolve([]);
    }

    return axios
        .get(`${baseURL}/customBreakdowns/labels`, {
            params: {
                ids: labelIds,
                viewId,
                teamspaceId,
            },
        })
        .then(propData);
}

function setDefaultPreset(id) {
    return axios.post(`${baseURL}/presets/${id}/setDefault`).then(propData);
}

function createView({
    config,
    userAccountId,
    type = 'view',
    subtype = 'normal',
    parent_id,
    order,
    originalViewId = undefined,
    view_title,
}) {
    if (parent_id) {
        parent_id = Number(parent_id);
    }

    let data = {
        config,
        type,
        subtype,
        parent_id,
        order,
        view_title,
    };

    if (originalViewId !== undefined) {
        originalViewId = Number(originalViewId);
        data = R.compose(
            // TODO: delete one of them => check
            R.assocPath(['originalViewId'], originalViewId),
            R.assocPath(['original_view_id'], originalViewId)
        )(data);
    }

    return axios
        .post(`${baseURL}/customViews`, data, {
            params: { userAccountId },
        })
        .then(propData);
}

function getViews(query = {}) {
    return axios
        .get(`${baseURL}/customViews?${qs.stringify(query)}`)
        .then(propData);
}

function saveView({ view, userAccountId }) {
    const data = R.pick(['viewTitle', 'config', 'order'], view);

    return axios
        .patch(`${baseURL}/customViews/v3/${view.id}`, data, {
            params: { userAccountId },
        })
        .then(propData);
}

function deleteView({ id, userAccountId }) {
    return axios
        .delete(`${baseURL}/customViews/${id}`, { params: userAccountId })
        .then(propData);
}

function changeViewOrders(order) {
    return axios
        .patch(`${baseURL}/customViews/orders`, { order })
        .then(propData);
}

function fetchAdAccountAssets(queryObj = {}) {
    return axios
        .get(`${baseURL}/adAccounts?${qs.stringify(queryObj)}`)
        .then(propData);
}

function createExcelJob({ query = {}, dashboardFilter = {} }) {
    return axios
        .post(`${baseURL}/excelJob?${qs.stringify(query)}`, { dashboardFilter })
        .then(propData);
}

function getExcelJob(id) {
    return axios.get(`${baseURL}/excelJob/${id}`).then(propData);
}

function syncCache(id) {
    return axios
        .post(`${baseURL}/syncArchitectureJobs?userAccountId=${id}`)
        .then(propData);
}

function checkSyncCache(id) {
    return axios
        .get(`${baseURL}/syncArchitectureJobs?userAccountId=${id}`)
        .then(propData);
}

function createBreakdown({ data, userAccountId }) {
    return axios
        .post(
            `${baseURL}/customBreakdowns?userAccountId=${userAccountId}`,
            data
        )
        .then(propData);
}

function getView({ id, query = {} } = {}) {
    if (!id) {
        return;
    }
    return axios
        .get(`${baseURL}/customViews/${id}?${qs.stringify(query)}`)
        .then(propData);
}

function getTeamspace({ id } = {}) {
    if (!id) {
        return;
    }
    return axios.get(`${baseURL}/teamspaces/${id}`).then(propData);
}

function getTeamspacesManagement() {
    return axios.get(`${baseURL}/teamspaces/management`).then(propData);
}

function updateViewPermissions({
    isPrivate,
    userIds,
    pendingUserEmails,
    teamIds,
    id,
} = {}) {
    return axios
        .post(`${baseURL}/customViews/${id}/permissions`, {
            isPrivate,
            userIds,
            pendingUserEmails,
            teamIds,
        })
        .then(propData);
}

function getCampaignIdList() {
    return axios.get(`${baseURL}/campaigns`).then(propData);
}

function getUnlabelledCampaigns(breakdownId, assetIds = []) {
    const params = isValueExist(assetIds)
        ? {
              assetIds: assetIds.join(','),
          }
        : undefined;

    return axios
        .get(`${baseURL}/customBreakdowns/${breakdownId}/unlabelledList`, {
            params,
        })
        .then(propData);
}

function createPdfJob({
    dashboardId = '',
    query = {},
    dashboardFilter = {},
    queryString,
}) {
    return axios
        .post(`${baseURL}/pdfJob?${qs.stringify(query)}`, {
            dashboardId,
            dashboardFilter,
            queryString,
        })
        .then(propData);
}

function getPdfJob(id) {
    return axios.get(`${baseURL}/pdfJob/${id}`).then(propData);
}

function getElementByTitle({ level, title }) {
    return axios
        .get(`${baseURL}/${level}/elements?title=${title}`)
        .then(propData);
}

function getFieldPreferences() {
    return axios.get(`${baseURL}/fieldPreferences`).then(propData);
}

/**
 * @description Fetches selected fields information from BE for the current list keys.
 */
function searchAvailableFieldsV3({
    assetIds,
    breakdowns,
    fieldType,
    widgetType,
    language,
    fieldKeysSearch,
    isForDatastream,
    teamspaceId,
    viewId,
}) {
    return axios
        .post(`${baseURL}/dataSource/availableFields/v3/search`, {
            assetIds,
            breakdowns,
            fieldType,
            widgetType,
            language,
            fieldKeysSearch,
            isForDatastream,
            teamspaceId,
            viewId,
        })
        .then(propData);
}

const getAvailableFieldsV2 = memoizee(
    ({
        assetIds,
        language,
        widgetType,
        include,
        breakdowns,
        isForDatastream,
    }) => {
        return axios
            .post(`${baseURL}/dataSource/availableFields/v2`, {
                assetIds,
                language,
                widgetType,
                include,
                breakdowns,
                isForDatastream,
            })
            .then(propData);
    },
    {
        maxAge: 1000 * 60 * 2,
        max: 3,
        normalizer: JSON.stringify,
    }
);

const getCreativeAvailableDimensions = ({ assetIds = [], widgetType }) => {
    return axios
        .post(`${baseURL}/dataSource/creativeWidgetAvailableDimensions`, {
            assetIds,
            widgetType,
        })
        .then(propData);
};

const getCompatibility = ({ selectedDatasources, selectedBreakdowns }) => {
    return axios
        .post(`${baseURL}/dataSource/compatibility`, {
            selectedDatasources,
            selectedBreakdowns,
        })
        .then(propData);
};

function fetchViewKey(viewId) {
    return axios.get(`${baseURL}/customViews/${viewId}/viewKey`).then(propData);
}

const getAutoDateFormat = ({ assetId, dateFieldDimension }) => {
    const params = new URLSearchParams({
        assetId,
        dateField: dateFieldDimension,
    });

    return axios.get(`${baseURL}/dataSource/guessDateFormat`, { params });
};

const getDimensionsByWidgetDimension = ({ widgetDimension }) => {
    return axios.get(
        `${baseURL}/blendData/dimensionOptions?widgetDimension=${widgetDimension}`
    );
};

const getNotepadWidgetData = ({ viewId, widgetId }) => {
    const url = `${baseURL}/customViews/${viewId}/notepadWidget/${widgetId}`;
    return axios.get(url).then(propData);
};

const updateNotepadWidgetData = ({
    viewId,
    widgetId,
    content,
    callback: handleSaveProgress,
}) => {
    const url = `${baseURL}/customViews/${viewId}/notepadWidget/${widgetId}`;
    const body = { content };
    const config = {
        onUploadProgress: (progressEvent) => {
            if (!handleSaveProgress) return;
            const progress = parseInt(
                Math.round((progressEvent.loaded * 100) / progressEvent.total)
            );
            handleSaveProgress(progress);
        },
    };

    return axios.post(url, body, config).then(propData);
};

// TODO: widget error fetchAdAccountAssets: new version
const getDataSourcesV3 = () => {
    const url = `${baseURL}/dataSource`;
    return axios.get(url).then(propData);
};

const getDataSource = (assetId) => {
    const url = `${baseURL}/dataSource/${assetId}`;
    return axios.get(url).then(propData);
};

const getDataSourceV1 = () => {
    return axios.get(`${baseURL}/connect/adAccounts`).then(propData);
};

const updateDataSourceSettings = ({ assetId, settings }) => {
    return axios
        .put(`${baseURL}/dataSource/${assetId}/dataSourceSettings`, settings)
        .then(propData);
};

const refreshGss = (assetId) => {
    // https://api.adriel.com/api/dashboard/dataSource/refreshGSSCache
    return axios
        .post(`${baseURL}/dataSource/refreshGSSCache`, { assetId })
        .then(propData);
};

const getCustomMetrics = (viewId) => {
    return axios
        .get(`${baseURL}/customMetrics?viewId=${viewId}`)
        .then(propData);
};

export default {
    getGraph,
    getPreview,
    getListFilterPreview,
    saveFilters,
    getSavedFilters,
    deleteSavedFilter,
    createPreset,
    getPresets,
    setDefaultPreset,
    deletePreset,
    createView,
    fetchAdAccountAssets,
    getViews,
    saveView,
    deleteView,
    createExcelJob,
    getExcelJob,
    changeViewOrders,
    syncCache,
    checkSyncCache,
    getCustomBreakdowns,
    searchCustomBreakdownLabels,
    getCustomBreakdownById,
    resolveLabels,
    createBreakdown,
    getView,
    getTeamspace,
    getTeamspacesManagement,
    updateViewPermissions,
    getCampaignIdList,
    getUnlabelledCampaigns,
    createPdfJob,
    getPdfJob,
    getElementByTitle,
    getAvailableFieldsV2,
    getCreativeAvailableDimensions,
    getCompatibility,
    fetchViewKey,
    getAutoDateFormat,
    getDimensionsByWidgetDimension,
    getNotepadWidgetData,
    updateNotepadWidgetData,
    getDataSourcesV3,
    getDataSource,
    getDataSourceV1,

    updateDataSourceSettings,
    refreshGss,

    getFieldPreferences,
    getCustomMetrics,
    searchAvailableFieldsV3,
};
